<!-- =========================================================================================
  File Name: Products.vue
  Description: Products' list
  ----------------------------------------------------------------------------------------
  Item Name: FastWay - Products list for management
  Author: Oscar Ramírez O.
  Author URL: https://acentoNET.com
========================================================================================== -->

<template>

  <div>
    <vs-button radius color="danger" type="filled" icon-pack="feather" icon="icon-plus" class="add-button" :to="{name: 'product', params: { id: 0 }}"/>

    <div class="vx-card p-6">
      <ejs-grid
        id="productsGrid"
        ref="grid"
        :dataSource="data"
        locale='es-GT'
        :toolbar='toolbarOptions'
        :allowPaging="true"
        :pageSettings='pageSettings'
        :allowSorting='true'
        :sortSettings='sortOptions'
        :allowExcelExport='true'
        :toolbarClick='toolbarClick'
        :allowFiltering='true'
        :filterSettings='filterOptions'
        :searchSettings='searchOptions'
        :enablePersistence='true'
      >
        <e-columns>
          <e-column field="id" headerText="ID" :isPrimaryKey="true" width="100" :template="cTemplate" displayValue="id" routeName="product" urlBase="/catalogs/product/"/>
          <e-column headerText='Imagen' width='100' :template='cTemplateImage' image="smallImage" altImage="name" :imagesURL="imagesURL" imageHeight="55"></e-column>
          <e-column field='isbn' headerText='Código' width="150" :template="cTemplate" displayValue="isbn" routeName="product" urlBase="/catalogs/product/"/>
          <e-column field="name" headerText="Nombre" :template="cTemplate" displayValue="name" routeName="product" urlBase="/catalogs/product/"/>
          <e-column field="price" headerText="Precio" width="120"/>
          <e-column field="categoryName" headerText="Categoría" width="200" :filter='checkBoxFilter'/>
          <e-column field="statusName" headerText="Estado" width="150" :template='cTemplateStatus' :filter='checkBoxFilter'/>
          <e-column field="available" headerText="Disponible" width="150" :template='checkBoxTemplate' checked="available" :filter='checkBoxFilter'/>
          <e-column field="onSale" headerText="Promoción" width="150" :template='checkBoxTemplate' checked="onSale" :filter='checkBoxFilter'/>
        </e-columns>
      </ejs-grid>
    </div>
  </div>

</template>

<script>
  import Vue from 'vue';
  import {loadCldr, L10n, setCulture} from '@syncfusion/ej2-base';
  import {GridPlugin, Toolbar, Search, Page, Sort, ExcelExport, Filter} from '@syncfusion/ej2-vue-grids';
  import numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
  import gregorian from 'cldr-data/main/es-GT/ca-gregorian.json';
  import numbers from 'cldr-data/main/es-GT/numbers.json';
  import timeZoneNames from 'cldr-data/main/es-GT/timeZoneNames.json';
  import weekData from 'cldr-data/supplemental/weekData.json';// To load the culture based first day of week
  import CellImage from '@/views/components/CRImage';
  import CellRendererLink from '@/views/components/CRLinkS';
  import CheckBoxCellTemplate from '@/views/components/CheckBoxCellTemplate';
  import CellStatus from '@/views/components/CRStatus';

  Vue.use(GridPlugin);

  loadCldr(numberingSystems, gregorian, numbers, timeZoneNames, weekData);
  setCulture('es-GT');
  L10n.load({
    'es-GT': {
      'grid': {
        'EmptyRecord': 'No se encontraron productos',
        'Search': 'Buscar',
        'Excelexport': 'Exportar'
      },
      'pager': {
        'currentPageInfo': '{0} de {1} páginas',
        'firstPageTooltip': 'Ir a primera página',
        'lastPageTooltip': 'Ir a última página',
        'nextPageTooltip': 'Ir a página siguiente',
        'previousPageTooltip': 'Ir a página anterior'
      }
    }
  });

  export default {
    data() {
      return {
        pageSettings: {pageSize: 10, currentPage: 1},
        toolbarOptions: [
          'Search',
          'ExcelExport',
          {
            text: 'Exportar Firebase',
            tooltipText: 'Exportar listado completo en formato JSON importable a Firebase',
            prefixIcon: 'e-csvexport e-icons',
            id: 'firebaseexport'
          }
        ],
        sortOptions: {columns: [{field: 'name', direction: 'Ascending'}, {field: 'code', direction: 'Ascending'}]},
        statusId: null,
        loaded: false,
        cTemplate: function () {
          return {template: CellRendererLink, urlBase: '/catalogs/product/'};
        },
        cTemplateImage: function () {
          return {template: CellImage};
        },
        cTemplateStatus: function () {
          return {template: CellStatus};
        },
        checkBoxTemplate: function () {
          return {template: CheckBoxCellTemplate};
        },
        filterOptions: {
          type: 'Menu'
        },
        searchOptions: {
          fields: [],
          operator: 'contains',
          key: '',
          ignoreCase: true
        },
        checkBoxFilter: {
          type: 'CheckBox'
        }
      }
    },
    components: {
      CellImage,
      CellRendererLink
    },
    provide: {
      grid: [Toolbar, Search, Page, Sort, ExcelExport, Filter]
    },
    computed: {
      imagesURL() {
        return this.$store.getters['getParameter']('images').baseURL;
      },
      categories() {
        return this.$store.getters.categoriesList;
      },
      data() {
        return this.$store.getters.productsList;
      }
    },
    methods: {
      toolbarClick: function (args) {
        if (args.item.id.includes('excelexport')) {
          const excelExportProperties = {
            fileName: "ListadoProductos.xlsx"
          };
          this.$refs.grid.excelExport(excelExportProperties);
        }
        if (args.item.id.includes('firebaseexport')) {
          this.exportFirebase();
        }
      },
      exportFirebase() {
        // Se exporta el documento crudo del state (tal como está en el documento
        // catalogs/products de Firestore) y no el getter productsList, para que
        // el archivo pueda reimportarse a Firebase sin los campos calculados por
        // el getter (categoryName, statusName, searchField, originalPrice, etc.)
        // y sin el metadato 'id' que agrega vuexfire al documento.
        const productsDoc = this.$store.state.products;
        if (!productsDoc || !productsDoc.list) {
          this.$vs.notify({
            title: 'Sin datos',
            text: 'El listado de productos aún no ha sido cargado',
            color: 'warning'
          });
          return;
        }
        const exportData = {
          sequence: productsDoc.sequence || 0,
          list: productsDoc.list
        };
        const blob = new Blob([JSON.stringify(exportData, null, 2)], {type: 'application/json'});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'ListadoProductosFirebase.json';
        link.click();
        URL.revokeObjectURL(link.href);
        this.$vs.notify({
          title: 'Exportación completada',
          text: 'El listado completo de productos fue exportado en formato JSON importable a Firebase',
          color: 'primary'
        });
      },
      edit(id) {
        this.$router.push(`/catalogs/product/${id}`).catch(() => {
        });
      }
    }
  }

</script>

<style lang="scss">
  @import '../../../../node_modules/@syncfusion/ej2-base/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-inputs/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-navigations/styles/material.css';
  @import "../../../../node_modules/@syncfusion/ej2-vue-grids/styles/material.css";
  @import '../../../../node_modules/@syncfusion/ej2-dropdowns/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-popups/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-buttons/styles/material.css';

  .add-button {
    position: fixed;
    bottom: 5%;
    right: 29px;
    z-index: 51000;
    box-shadow: 0 1px 20px 1px rgb(251, 67, 79);
  }
</style>
